import { SUPPLY_IDS, SupplyData, SupplyId, SuppliesData, isSupplyId } from "../data/supplies";
import { NO_SUPPLIES_RULE, SuppliesRule } from "../sim/modifiers";
import type { BalanceData } from "../data/balance";
import { chestShare } from "./orders";
import type { PlayerProfile } from "./profile";

/**
 * Pre-battle supplies (MP-06, docs/SHOP.md §14): War Banner, Scout's Map, Supply Wagon and Rally Horn. Bought in the
 * hub's Shop for gold (since 2026-10-23; gems before) or won (Daily Orders, event nodes), held in the `consumables` save section, armed on the battle
 * card (at most `perBattle` of each) and taken when a battle starts: the armed ones become the run's SuppliesRule (part
 * of its modifiers, so of the replay record and the hash) and leave the stock. Nothing is refunded once a battle has
 * started; a battle that never starts (backed out of the load) takes nothing. Pure and deterministic.
 */

export interface SupplyStockJson { have: Record<string, number>; armed: string[]; twist?: boolean }

/** What arming did: armed, disarmed, or why not. */
export type ArmResult = "armed" | "disarmed" | "none" | "unknown";

export class SupplyStock {
  private readonly counts = new Map<SupplyId, number>();
  private readonly armedSet: SupplyId[] = [];
  /** The farm-twist switch on the battle card (MP-25): replays of cleared levels take the day's twist while on. */
  twist = false;

  count(id: SupplyId): number { return this.counts.get(id) ?? 0; }
  isArmed(id: SupplyId): boolean { return this.armedSet.includes(id); }
  get armed(): readonly SupplyId[] { return SUPPLY_IDS.filter((id) => this.armedSet.includes(id)); }
  /** Supplies held in all. */
  get total(): number { let n = 0; for (const v of this.counts.values()) n += v; return n; }

  /** Adds up to `n`, never past `maxOwned`; returns how many were added. */
  add(data: SuppliesData, id: SupplyId, n: number): number {
    if (!Number.isInteger(n) || n <= 0 || data.maxOwned <= 0) return 0;
    const before = this.count(id), after = Math.min(data.maxOwned, before + n);
    this.counts.set(id, after);
    return after - before;
  }

  /** The battle card's well: arms one held supply (perBattle of each at most: one), or disarms it. */
  toggle(id: SupplyId): ArmResult {
    if (!isSupplyId(id)) return "unknown";
    const i = this.armedSet.indexOf(id);
    if (i >= 0) { this.armedSet.splice(i, 1); return "disarmed"; }
    if (this.count(id) <= 0) return "none";
    this.armedSet.push(id);
    return "armed";
  }

  /**
   * A battle starts: the armed supplies it can use leave the stock and become its rule. A run without a checkpoint
   * retry (Iron, Endless) leaves the War Banner armed and held. The rest of the armed set is cleared: each battle is
   * armed afresh.
   */
  take(data: SuppliesData, opts: { readonly checkpoint: boolean }): SuppliesRule {
    let rule: SuppliesRule = NO_SUPPLIES_RULE;
    const kept: SupplyId[] = [];
    for (const id of this.armed) {
      const item = data.items.find((x) => x.id === id);
      if (item === undefined || this.count(id) <= 0) continue;
      if (id === "warBanner" && !opts.checkpoint) { kept.push(id); continue; }
      this.counts.set(id, this.count(id) - 1);   // perBattle is 1: one of each into a battle
      rule = withSupply(rule, item);
    }
    this.armedSet.length = 0;
    this.armedSet.push(...kept);
    return rule;
  }

  toJson(): SupplyStockJson {
    const have: Record<string, number> = {};
    for (const id of SUPPLY_IDS) if (this.count(id) > 0) have[id] = this.count(id);
    return { have, armed: [...this.armed], ...(this.twist ? { twist: true } : {}) };
  }

  /** Replaces this stock with a stored one, leniently field by field: an unknown id or a bad count is dropped, never the rest. */
  load(j: unknown): void {
    this.counts.clear();
    this.armedSet.length = 0;
    this.twist = false;
    if (typeof j !== "object" || j === null) return;
    const src = j as Partial<SupplyStockJson>;
    if (typeof src.have === "object" && src.have !== null) {
      for (const [k, v] of Object.entries(src.have)) if (isSupplyId(k) && Number.isInteger(v) && v > 0) this.counts.set(k, v);
    }
    if (Array.isArray(src.armed)) for (const a of src.armed) if (isSupplyId(a) && this.count(a) > 0 && !this.armedSet.includes(a)) this.armedSet.push(a);
    this.twist = src.twist === true;
  }

  static fromJson(j: unknown): SupplyStock { const s = new SupplyStock(); s.load(j); return s; }
}

/** The rule with one more supply's effect in it. */
export function withSupply(rule: SuppliesRule, item: SupplyData): SuppliesRule {
  switch (item.id) {
    case "warBanner": return { ...rule, checkpoints: rule.checkpoints + item.amount };
    case "scoutsMap": return { ...rule, scout: rule.scout + item.amount };
    case "supplyWagon": return { ...rule, rerolls: rule.rerolls + item.amount };
    case "rallyHorn": return { ...rule, hornTroops: rule.hornTroops + item.amount, hornWaves: Math.max(rule.hornWaves, item.waves) };
  }
}

/** The rule of every supply at once (tests, goldens and captures). */
export function allSuppliesRule(data: SuppliesData): SuppliesRule {
  return data.items.reduce(withSupply, NO_SUPPLIES_RULE);
}

/** A supply's gold price for a player whose highest cleared level is `level` (never under 1 gold). */
export function supplyGold(balance: BalanceData, item: SupplyData, level: number): number {
  return Math.max(1, chestShare(balance, Math.max(1, level), item.goldChests));
}

/**
 * Buys one supply for gold (owner 2026-10-23; hub only: the caller checks the screen). Spends once, then adds; refused
 * when the stock is at `maxOwned` (nothing spent) or the gold is short.
 */
export function buySupply(stock: SupplyStock, profile: PlayerProfile, data: SuppliesData, id: SupplyId, level: number): true | string {
  const item = data.items.find((x) => x.id === id);
  if (item === undefined) return "unknown supply";
  if (stock.count(id) >= data.maxOwned) return "you hold all you can carry";
  const cost = supplyGold(profile.balance, item, level);
  if (!profile.canAfford(cost)) return "not enough gold";
  profile.addGold(-cost);
  stock.add(data, id, 1);
  return true;
}
